// src/components/agents/AgentForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, MenuItem, Switch,
  FormControlLabel, CircularProgress, Alert, Tooltip,
  Autocomplete, Grid,
} from '@mui/material';
import { toast } from 'react-toastify';

import { toolService } from '../../api/toolService';
import { useModels } from '../../hooks/useModels';
// Import promptService if AgentForm itself needs to fetch prompts,
// but for now, we'll pass them as props from AgentEditor/AgentSettingsTab.

const FALLBACK_MODELS = ['gpt-4', 'gpt-3.5-turbo'];
const DEFAULT_TEMPERATURE = 0.7;
const DEFAULT_MAX_TOKENS = 2048;

const initialFormStateDefaults = {
  name: '',
  description: '',
  llm_model: '',
  is_public: false,
  agent_settings_object: { temperature: DEFAULT_TEMPERATURE, max_tokens: DEFAULT_MAX_TOKENS },
  agent_settings_json_string: JSON.stringify({ temperature: DEFAULT_TEMPERATURE, max_tokens: DEFAULT_MAX_TOKENS }, null, 2),
  tool_slugs: [],
  initial_prompt_id_to_link: '', // New: For selecting prompt during creation
};

const AgentForm = ({
  initialData,
  isEditing,
  onSubmit,
  isSaving,
  availablePrompts = [], // New prop
  loadingPrompts = false,  // New prop
}) => {
  const [form, setForm] = useState(initialFormStateDefaults);
  const [formErrors, setFormErrors] = useState({});
  const [localError, setLocalError] = useState(null);

  const [modelOptions, loadingModels, modelError] = useModels(FALLBACK_MODELS);
  const [allToolsForOptions, setAllToolsForOptions] = useState([]);
  const [loadingAllToolsOptions, setLoadingAllToolsOptions] = useState(true);

  useEffect(() => {
    if (isEditing && initialData) {
      const currentAgentSettings = initialData.agent_settings || {};
      setForm({
        name: initialData.name || '',
        description: initialData.description || '',
        llm_model: initialData.llm_model || '',
        is_public: initialData.is_public || false,
        agent_settings_object: currentAgentSettings,
        agent_settings_json_string: JSON.stringify(currentAgentSettings, null, 2),
        tool_slugs: initialData.tool_slugs || [],
        initial_prompt_id_to_link: '', // Not used in edit mode directly here
      });
    } else {
      const defaultState = { ...initialFormStateDefaults };
      if (modelOptions.length > 0 && !defaultState.llm_model) {
        defaultState.llm_model = modelOptions[0]?.id || '';
      }
      // If initialData is passed for creation (e.g., pre-fill from URL query params)
      if (!isEditing && initialData) {
          defaultState.name = initialData.name || defaultState.name;
          // Potentially pre-fill other fields if `initialData` contains them for creation
          defaultState.initial_prompt_id_to_link = initialData.initial_prompt_id_to_link || '';
      }
      setForm(defaultState);
    }
  }, [isEditing, initialData, modelOptions]);


  useEffect(() => {
    (async () => {
      setLoadingAllToolsOptions(true);
      const res = await toolService.getAllTools();
      if (!res.isError && Array.isArray(res)) {
        setAllToolsForOptions(res);
      } else {
        toast.error("Failed to load tool options for selection.");
        setAllToolsForOptions([]);
      }
      setLoadingAllToolsOptions(false);
    })();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLocalError(null);
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleAgentSettingChange = (settingName, settingValue) => {
    setForm(prevForm => {
      const newSettingsObj = { ...(prevForm.agent_settings_object || {}), [settingName]: settingValue };
      if (formErrors.agent_settings_json_string) {
        setFormErrors(prevErr => ({...prevErr, agent_settings_json_string: null}));
      }
      return {
        ...prevForm,
        agent_settings_object: newSettingsObj,
        agent_settings_json_string: JSON.stringify(newSettingsObj, null, 2),
      };
    });
  };
  
  const handleAgentSettingsJsonStringChange = (e) => {
    const newJsonString = e.target.value;
    setForm(prev => ({ ...prev, agent_settings_json_string: newJsonString }));
    try {
      const parsed = JSON.parse(newJsonString);
      setForm(prev => ({ ...prev, agent_settings_object: parsed }));
      if (formErrors.agent_settings_json_string) {
        setFormErrors(prev => ({ ...prev, agent_settings_json_string: null }));
      }
    } catch (err) {
      setFormErrors(prev => ({ ...prev, agent_settings_json_string: "Invalid JSON format." }));
    }
  };

  const handleToolSelectionChange = (event, newValue) => {
    setForm(prev => ({ ...prev, tool_slugs: newValue.map(tool => tool.slug) }));
  };

  const handleInitialPromptSelectionChange = (event, newValue) => {
    setForm(prev => ({ ...prev, initial_prompt_id_to_link: newValue ? newValue.id : '' }));
  };

  const validateForm = useCallback(() => {
    const errors = {};
    if (!form.name?.trim()) errors.name = 'Agent name is required.';
    if (!form.llm_model) errors.llm_model = 'LLM Model is required.';
    
    if (formErrors.agent_settings_json_string) {
        errors.agent_settings_json_string = formErrors.agent_settings_json_string;
    }
    const temp = form.agent_settings_object?.temperature;
    if (temp !== undefined && (isNaN(parseFloat(temp)) || parseFloat(temp) < 0 || parseFloat(temp) > 2)) {
        errors.temperature = "Temperature must be a number between 0 and 2.";
    }
    const maxTokens = form.agent_settings_object?.max_tokens;
    if (maxTokens !== undefined && (isNaN(parseInt(maxTokens)) || parseInt(maxTokens) <= 0)) {
        errors.max_tokens = "Max tokens must be a positive integer.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [form.name, form.llm_model, form.agent_settings_object, formErrors.agent_settings_json_string]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalError(null);
    setFormErrors(prev => ({ agent_settings_json_string: prev.agent_settings_json_string }));

    if (!validateForm()) {
      toast.error('Please correct the form errors.');
      return;
    }
    if (formErrors.agent_settings_json_string) {
        toast.error('Agent Settings JSON is invalid. Please correct it.');
        return;
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      llm_model: form.llm_model,
      is_public: form.is_public,
      agent_settings: form.agent_settings_object,
      tool_ids: form.tool_slugs, // Backend expects tool_ids
      // Conditionally add prompt ID if creating
      ...(!isEditing && form.initial_prompt_id_to_link && { initial_prompt_id_to_link: form.initial_prompt_id_to_link }),
    };
    onSubmit(payload);
  };

  if (loadingAllToolsOptions || (loadingModels && !modelError && !form.llm_model && !isEditing)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
        <Typography sx={{ml: 2}}>Loading form options...</Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      {localError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError(null)}>{localError}</Alert>}
      {modelError && (
         <Alert severity="warning" sx={{ mb: 2 }}>
           {`Error loading LLM models: ${modelError}. Default/fallback models may be shown.`}
         </Alert>
      )}

      <TextField
        fullWidth required margin="normal"
        label="Name" name="name" value={form.name} onChange={handleChange}
        disabled={isSaving} error={!!formErrors.name} helperText={formErrors.name}
        inputProps={{ maxLength: 100 }}
      />

      <TextField
        fullWidth margin="normal" multiline rows={3}
        label="Description" name="description" value={form.description} onChange={handleChange}
        disabled={isSaving} helperText="A brief description of the agent's purpose or capabilities."
        inputProps={{ maxLength: 500 }}
      />

      <TextField
        select fullWidth required margin="normal" label="LLM Model" name="llm_model"
        value={form.llm_model} onChange={handleChange} disabled={loadingModels || isSaving}
        error={!!formErrors.llm_model}
        helperText={ loadingModels ? 'Loading models…' : modelError ? `Could not load models: ${modelError}` : (formErrors.llm_model || 'Select the language model for this agent.') }
      >
        {!modelOptions.length && !loadingModels && <MenuItem value="" disabled><em>No models available</em></MenuItem>}
        {modelOptions.map((model) => (<MenuItem key={model.id} value={model.id}>{model.name || model.id}</MenuItem>))}
      </TextField>

      {!isEditing && (
        <Autocomplete
          id="initial-prompt-select"
          options={availablePrompts}
          getOptionLabel={(option) => option.name || `ID: ${option.id.substring(0,8)}...`}
          value={availablePrompts.find(p => p.id === form.initial_prompt_id_to_link) || null}
          onChange={handleInitialPromptSelectionChange}
          loading={loadingPrompts}
          disabled={isSaving || loadingPrompts}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="normal"
              label="Initial System Prompt (Optional)"
              helperText="Select an existing prompt to link and activate for this new agent."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingPrompts ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}

      <Typography variant="subtitle1" sx={{ mt: 2, mb: -1 }}>Model Settings</Typography>
      <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
              <TextField
                  fullWidth label="Temperature" name="temperature" type="number"
                  inputProps={{ step: 0.1, min: 0, max: 2 }}
                  value={form.agent_settings_object?.temperature ?? DEFAULT_TEMPERATURE}
                  onChange={(e) => handleAgentSettingChange('temperature', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                  margin="normal" disabled={isSaving} error={!!formErrors.temperature}
                  helperText={formErrors.temperature || "Controls randomness (e.g., 0.0 to 2.0)."}
              />
          </Grid>
          <Grid item xs={12} sm={6}>
              <TextField
                  fullWidth label="Max Tokens" name="max_tokens" type="number"
                  inputProps={{ step: 1, min: 1 }}
                  value={form.agent_settings_object?.max_tokens ?? DEFAULT_MAX_TOKENS}
                  onChange={(e) => handleAgentSettingChange('max_tokens', e.target.value === '' ? undefined : parseInt(e.target.value, 10))}
                  margin="normal" disabled={isSaving} error={!!formErrors.max_tokens}
                  helperText={formErrors.max_tokens || "Max response length."}
              />
          </Grid>
      </Grid>
      
      <Tooltip title="Advanced LLM parameters (e.g., {'top_p': 0.9}) in JSON format.">
        <TextField
          fullWidth margin="normal" multiline rows={6}
          label="Advanced Agent Settings (JSON)" name="agent_settings_json_string"
          value={form.agent_settings_json_string} onChange={handleAgentSettingsJsonStringChange}
          disabled={isSaving} InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.9rem' } }}
          error={!!formErrors.agent_settings_json_string}
          helperText={formErrors.agent_settings_json_string || "Override default model parameters like top_p, etc."}
        />
      </Tooltip>

      <Typography variant="subtitle1" sx={{ mt: 2, mb: 0 }}>Tools & Access</Typography>
      <Autocomplete
        multiple id="agent-tools-select-form" options={allToolsForOptions}
        getOptionLabel={(option) => `${option.name} (${option.slug})`}
        value={allToolsForOptions.filter(tool => form.tool_slugs && form.tool_slugs.includes(tool.slug))}
        onChange={handleToolSelectionChange}
        isOptionEqualToValue={(option, value) => option.slug === value.slug}
        loading={loadingAllToolsOptions} disabled={isSaving}
        renderInput={(params) => (
          <TextField
            {...params} margin="normal" label="Assigned Tools" placeholder="Select tools..."
            helperText="Tools this agent can use."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loadingAllToolsOptions ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      <FormControlLabel
        control={ <Switch checked={form.is_public} onChange={handleChange} name="is_public" disabled={isSaving}/> }
        label="Publicly Accessible" sx={{ mt: 1, mb: 1 }}
      />
      <Typography variant="caption" color="text.secondary" display="block" sx={{mb:1}}>
          If checked, this agent might be accessible via public-facing widgets or APIs.
      </Typography>
      
      <button type="submit" style={{ display: 'none' }} aria-hidden="true">Hidden Submit</button>
    </Box>
  );
};

export default AgentForm;